import queryString from 'query-string';

import { trackCustomEvent } from 'my-core/gtm-events';

import { trackingIdentity } from './posthog';

export type CalendlyTrackingParams = Readonly<{ salesforce_uuid: string; utm_term: string }>;
export type CalendlyDialogUtm = Readonly<{
  utmSource: 'website';
  utmMedium: string;
  utmContent: string;
  salesforce_uuid?: string;
  utmTerm?: string;
}>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Links whose mousedown was already handled; their following click is the same activation.
const armed = new WeakSet<HTMLAnchorElement>();
let installed = false;

// `page` and `section` become the utm_medium / utm_content the Calendly webhook reports the booking under.
export function calendlyUrl(path: string, page?: string, section?: string): string {
  return `https://calendly.com/${path}?${queryString.stringify({ utm_source: 'website', utm_medium: page, utm_content: section })}`;
}

export function calendlyTrackingParams(): CalendlyTrackingParams | null {
  const identity = trackingIdentity();
  const distinctId = identity?.distinctId;
  const sessionId = identity?.sessionId;
  if (typeof distinctId !== 'string' || distinctId.length === 0 || typeof sessionId !== 'string') return null;
  if (!UUID_PATTERN.test(sessionId)) return null;
  return { salesforce_uuid: distinctId, utm_term: sessionId };
}

export function decorateCalendlyHref(href: string, params: CalendlyTrackingParams | null): string {
  try {
    const url = new URL(href);
    if (url.hostname.toLowerCase() !== 'calendly.com') return href;
    if (params) {
      url.searchParams.set('salesforce_uuid', params.salesforce_uuid);
      url.searchParams.set('utm_term', params.utm_term);
    } else {
      url.searchParams.delete('salesforce_uuid');
      url.searchParams.delete('utm_term');
    }
    return url.toString();
  } catch {
    return href;
  }
}

// The coaching dialog's iframe URL: record the opening outside React's render, then read the
// current PostHog identity independently of event delivery.
export function calendlyDialogUtm(utmMedium: string, utmContent: string): CalendlyDialogUtm {
  trackCustomEvent('Calendly Dialog Opened', { calendly_utm_medium: utmMedium, calendly_utm_content: utmContent });
  const tracking = calendlyTrackingParams();
  return {
    utmSource: 'website',
    utmMedium,
    utmContent,
    ...(tracking && { salesforce_uuid: tracking.salesforce_uuid, utmTerm: tracking.utm_term }),
  };
}

function handleCalendlyActivation(event: MouseEvent): void {
  try {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = new URL(link.href);
    if (url.hostname.toLowerCase() !== 'calendly.com') return;

    // A primary-button activation is mousedown then click (however long the press); keyboard is click
    // only; middle and secondary buttons get no click, so they are not armed.
    if (event.type === 'click' && armed.delete(link)) return;
    if (event.type === 'mousedown' && event.button === 0) armed.add(link);

    const [hostSlug, eventSlug] = url.pathname.split('/').filter(Boolean);
    trackCustomEvent(
      'Calendly Link Clicked',
      {
        calendly_host_slug: hostSlug ?? null,
        calendly_event_slug: eventSlug ?? null,
        calendly_utm_medium: url.searchParams.get('utm_medium'),
        calendly_utm_content: url.searchParams.get('utm_content'),
        destination_url: `${url.origin}${url.pathname}`,
        link_target: link.target || null,
      },
      { sendInstantly: true },
    );
    const next = decorateCalendlyHref(link.href, calendlyTrackingParams());
    if (next !== link.href) link.href = next;
  } catch {
    // Tracking must never interrupt navigation.
  }
}

export function installCalendlyLinkTracking(): void {
  if (installed) return;
  document.addEventListener('mousedown', handleCalendlyActivation, true);
  document.addEventListener('click', handleCalendlyActivation, true);
  installed = true;
}
